import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useId } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ExtraTripsApprovalListSchema } from '@/constants/ApprovalConstants';
import api from '@/services/apiSgft';
import { userIsRhOrAdmin } from '@/utils/handleSavePermissions';
import EmptyIcon from '../../assets/icons/empty.svg';
import { columns } from '../../constants/tableColumns/ExtraTripsRequestsInProgressColumns';
import Spinner from '../atoms/Spinner';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import InfiniteTable from './InfiniteTable';

const ExtraTripsRequestsInProgressTab = () => {
	const isRhOrAdmin = !!userIsRhOrAdmin();
	const [searchParams, setSearchParams] = useSearchParams();
	const onlyMyFlowsId = useId();

	const onlyMyFlows = searchParams.get('showFromAllFlows') === 'false';
	const showFromAllFlowsParam = isRhOrAdmin && !onlyMyFlows;

	useEffect(() => {
		if (isRhOrAdmin) return;
		if (!searchParams.has('showFromAllFlows')) return;
		const next = new URLSearchParams(searchParams);
		next.delete('showFromAllFlows');
		setSearchParams(next, { replace: true });
	}, [isRhOrAdmin, searchParams, setSearchParams]);

	const handleOnlyMyFlowsChange = (checked: boolean) => {
		const next = new URLSearchParams(searchParams);
		next.set('showFromAllFlows', String(!checked));
		setSearchParams(next, { replace: true });
	};

	const fetchApprovals = async ({ pageParam = 1 }) => {
		try {
			const response = await api.get('approval/extra-trip/solicitation', {
				params: {
					size: 20,
					page: pageParam,
					type: 'in-progress',
					showFromAllFlows: showFromAllFlowsParam,
				},
			});
			const parsedData = ExtraTripsApprovalListSchema.parse(response.data);
			return parsedData;
		} catch (e) {
			toast.error('Erro ao carregar os dados', {
				theme: 'colored',
				toastId: 'error',
			});
			throw e;
		}
	};

	const { data, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage } =
		useInfiniteQuery(
			['in-progress-extra-trip-approvals', showFromAllFlowsParam],
			fetchApprovals,
			{
				getNextPageParam: (lastPage) => lastPage.nextPage,
			},
		);

	const pendingApprovals =
		data?.pages.flatMap((page) => page.extraTripSolicitationData) ?? [];
	return (
		<div className="w-full px-2 pt-2">
			{isRhOrAdmin && (
				<div className="m-3 flex items-center gap-2">
					<Checkbox
						id={onlyMyFlowsId}
						checked={onlyMyFlows}
						onCheckedChange={(checked) =>
							handleOnlyMyFlowsChange(checked === true)
						}
					/>
					<Label
						htmlFor={onlyMyFlowsId}
						className="cursor-pointer text-sm font-normal text-gray-800"
					>
						Mostrar apenas solicitações dos meus fluxos
					</Label>
				</div>
			)}
			{isLoading ? (
				<div className="flex h-full w-full items-center rounded-md bg-white">
					<Spinner size={50} />
				</div>
			) : pendingApprovals.length > 0 ? (
				<InfiniteTable
					hasNextPage={hasNextPage}
					data={pendingApprovals}
					columns={columns}
					fetchNextPage={fetchNextPage}
					isFetchingNextPage={isFetchingNextPage}
					alignTextLeft
				/>
			) : (
				<div className="flex w-full flex-col items-center justify-center py-10">
					<img src={EmptyIcon} className="w-40" alt="Empty icon" />
					<div className="flex flex-col items-center text-sm">
						<p>Nenhuma solicitação em andamento encontrada</p>
						<span className="font-semibold text-blue-800">
							Quando surgirem novas solicitações de aprovação para idas extras,
							elas aparecerão aqui!
						</span>
					</div>
				</div>
			)}
		</div>
	);
};

export default ExtraTripsRequestsInProgressTab;
